/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, DatePicker, Input, Layout, Row, Select, Typography } from 'antd';
import { omit, startCase } from 'lodash';
import queryString from 'query-string';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';
import ReviewInspection from 'services/reviewInspection';

import { convertNumberToRupiahCurrency, errHandling } from 'utils';
import { INSPECTION_STATUS } from './constant';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;
const { Text } = Typography;

export function CheckingInspection(props) {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [endDatePick, setEndDatePick] = useState('');
  const [inspectionList, setInspectionList] = useState([]);
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDatePick] = useState('');
  const [status, setStatus] = useState('');

  const dateFormat = 'YYYY/MM/DD';
  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const inspectionStatusList = [
    {
      label: 'All',
      value: '',
    },
    {
      label: 'Checking',
      value: INSPECTION_STATUS.CHECK,
    },
    {
      label: 'Done',
      value: INSPECTION_STATUS.DONE,
    },
  ];

  const buttonAction = (row = {}) => {
    const { status } = row;
    return (
      <Row className="button-table">
        <Col lg={24}>
          <PdButton
            className="height-32"
            full
            onClick={handleToCheckInspectionDetail.bind(this, row)}
            type={status === INSPECTION_STATUS.CHECK ? 'danger' : 'primary'}>
            {status === INSPECTION_STATUS.CHECK ? 'Check' : 'Detail'}
          </PdButton>
        </Col>
      </Row>
    );
  };

  const columns = [
    {
      name: 'Order Id',
      selector: row => row.formatted_id,
      center: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => startCase(row.status),
      center: true,
    },
    {
      name: 'Request Date',
      selector: row => row.request_inspect_at,
      format: (row) => {
        const momentDate = dayjs(row.request_inspect_at);
        return momentDate.isValid() ? momentDate.format(dateFormat) : '-';
      },
      center: true,
    },
    {
      name: 'Total Price',
      selector: row => row.damage_penalty,
      format: (row) =>
        row.status === INSPECTION_STATUS.DONE
          ? convertNumberToRupiahCurrency(+row.damage_penalty)
          : '-',
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function handleToCheckInspectionDetail({
    id: inspectionId,
    status: inspectionStatus,
  }) {
    const search = queryString.stringify({
      id: inspectionId,
      status: inspectionStatus,
    });
    navigate({
      pathname: '/review_inspections/detail',
      search,
    });
  }

  async function fetchInspection({
    inspect_end_date,
    inspect_start_date,
    limit = 10,
    page,
    q,
    status,
  } = {}) {
    setLoadStatus(true);
    try {
      const { data: { data, metadata: { next_cursor } = {} } = {} } =
        await ReviewInspection.getReviewInspectionList({
          cursor: page,
          inspect_end_date,
          inspect_start_date,
          limit,
          q,
          status,
        });

      setInspectionList(data);
      setNextPage(next_cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed.page = page;
    const search = queryString.stringify(parsed);
    navigate({
      pathname: '/review_inspections',
      search,
    });
  }

  function filterInspection(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed.page = 1;
    const search = queryString.stringify(parsed);
    navigate({
      pathname: '/review_inspections',
      search,
    });
  }

  function resetFilter() {
    const search = queryString.stringify(
      omit(parsed, ['page', 'q', 'status', 'startDate', 'endDate'])
    );
    navigate({
      pathname: '/review_inspections',
      search,
    });
  }

  function handleOnChangeSearch({ target: { value } = {} } = {}) {
    setSearchValue(value);
  }

  function onChangeDate(_, datesString) {
    const [startDate, endDate] = datesString;
    if (!startDate) delete parsed['startDate'];
    else parsed['startDate'] = `${startDate} 00:00`;

    if (!endDate) delete parsed['endDate'];
    else parsed['endDate'] = `${endDate} 23:59`;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/review_inspections',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed.page) {
      if (parsed.page < 1) {
        parsed.page = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/review_inspections',
          search: stringified,
        });
      } else setCurrentPage(parsed.page);
    } else setCurrentPage(1);

    if (parsed.q) setSearchValue(parsed.q);
    else setSearchValue('');

    if (parsed.status) setStatus(parsed.status);
    else setStatus('');

    if (parsed.endDate) setEndDatePick(parsed.endDate);
    else setEndDatePick('');

    if (parsed.startDate) setStartDatePick(parsed.startDate);
    else setStartDatePick('');

    fetchInspection({
      page: parsed.page,
      q: parsed.q,
      status: parsed.status,
      inspect_start_date: parsed.startDate,
      inspect_end_date: parsed.endDate,
    });
  }, [parsed.page, parsed.q, parsed.status, parsed.startDate, parsed.endDate]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-checking-inspection">
      <div className="content">
        <div className="pageTitle">Checking Inspection</div>
        <Content className="pageContent">
          <Row
            gutter={[18, 36]}
            className="pd-margin-bottom-md"
            type="flex"
            align="middle">
            <Col lg={7}>
              <Search
                value={searchValue}
                onChange={handleOnChangeSearch}
                enterButton
                className="search-product"
                placeholder="Search Inspection"
                onSearch={filterInspection.bind(this, 'q')}
              />
            </Col>
            <Col lg={3}>
              <Select
                className="w-full"
                onChange={filterInspection.bind(this, 'status')}
                value={status}>
                {inspectionStatusList.map(({ label, value } = {}, idx) => (
                  <Option key={idx} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={4}>
              <Text className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </Text>
            </Col>
          </Row>

          <Row className="pd-margin-bottom-md">
            <Col lg={12}>
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={inspectionList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { CheckingInspection };
